/**
 * Údarás na Gaeltachta Brand Configuration
 * 
 * Brand-specific settings for the HubChat chatbot application
 * when running under the Údarás na Gaeltachta brand.
 */

export const udarasBrandConfig = {
  // Application branding
  defaultLanguage: 'ga' as const,
  appName: 'HubChat - AI na Gaeilge',
  appTitle: 'HubChat - AI na Gaeilge',
  appDescription: 'D\'fhoinse eolais chruinn faoi sheirbhísí poiblí, faoi rialacháin, faoi shochair agus faoi chearta.',
  websiteLink: 'https://www.udaras.ie/',
  
  // Title formatting - allows brand-specific styling without hardcoding in components
  titleFormatting: {
    // No special formatting for Údarás - displayed as-is
    appNameParts: null,
    heroTitleParts: null
  },
  
  // Header configuration
  header: {
    showLogo: true,
    logoVariant: 'white' as const,
    showAppName: true,
    navigationStyle: 'government' as const, // 'government' | 'modern' | 'minimal'
    logoWidth: 160, // Logo size with height constraint to prevent overflow
    voiceSettings: {
      buttonTitle: 'Socruithe Gutha',
      voiceSelectionLabel: 'Roghú Gutha',
      briefLabel: 'Freagra Gairid',
      prefetchLabel: 'Réamhshintéisiú fuaime',
      speedLabel: 'Luas athsheinm',
      speedOptions: {
        normal: 'Gnáthluas',
        faster: 'Tapúla'
      },
      selectedPrefix: 'Roghnaithe: ',
      defaultVoice: 'Guth Réamhshocraithe'
    },
    languageSwitcher: {
      groupLabel: 'Roghnaitheoir teanga',
      englishLabel: 'Béarla',
      irishLabel: 'Gaeilge',
      englishShortLabel: 'EN',
      irishShortLabel: 'GA',
      englishAriaLabel: 'Athraigh an teanga go Béarla',
      irishAriaLabel: 'Athraigh an teanga go Gaeilge',
    }
  },
  
  // Color scheme - Government Blue Palette
  colors: {
    primary: 'var(--brand-60)',      // Údarás Government Blue
    secondary: 'var(--brand-70)',     // Darker blue for accents
    accent: 'var(--brand-accent-orange)',        // Orange accent for highlights
    header: {
      background: 'var(--brand-60)',
      text: 'var(--neutral-0)',
      brandBackground: 'var(--brand-accent-60)',
      brandText: 'var(--neutral-0)',
    }
  },
  
  // Design system
  design: {
    borderRadius: 'rounded', // 'sharp' | 'rounded' | 'mixed'
    borderStyle: 'soft', // 'angular' | 'soft' | 'mixed'
    shadowStyle: 'subtle', // 'subtle' | 'prominent' | 'minimal'
    typography: 'modern', // 'government' | 'modern' | 'classic'
  },
  
  // Layout preferences
  layout: {
    headerHeight: '60px',
    contentPadding: 'var(--space-4)',
    cardSpacing: 'var(--space-4)',
    footerStyle: 'ghost' as const, // 'ghost' | 'default' - ghost style has transparent background, white text
    hideBottomInputOnWelcome: true, // Hide bottom chat input when welcome screen is shown
    chatInputContainerStyle: 'floating' as const, // 'floating' | 'default' - floating has transparent container
    backgroundOverlay: 'linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55))', // Dark overlay for better text contrast
  },
  
  // Feature flags
  features: {
    showLanguageSwitcher: false,
    enableVoiceInput: true,
    enableVoiceSynthesis: true,  // Toggle feature - set to true to enable
    enableBriefResponse: true, // Enable brief response toggle for Údarás
    speechProvider: 'abair' as const,
    abairWebSocketUrl: 'wss://recognition-testing-api.abair.ie',
    synthesisVoices: {
      ga: 'ga_CO_snc_piper',
    },
  },
  
  // Footer configuration
  footer: {
    // Left section: Privacy statement link
    leftText: 'Ráiteas Príobháideachta',
    leftLink: undefined,
    
    // Center section: Brand copyright
    centerText: 'Údarás na Gaeltachta',
    centerLink: 'https://www.udaras.ie/',
    
    // Right section: About link with version (APP_VERSION is always appended)
    rightText: '',
    rightLink: undefined,
  },
  
  // Hero section content
  hero: {
    title: 'Fáilte go HubChat',
    subtitle: 'D\'fhoinse eolais chruinn faoi sheirbhísí poiblí, faoi rialacháin, faoi shochair agus faoi chearta.',
    tryAskingLabel: 'Níl a fhios agat cá háit le tosú? Seo roinnt smaointe:',
    showInputField: true, // Show input field on welcome screen
    inputFieldPosition: 'top' as const, // 'top' | 'bottom' - position relative to suggested questions
    suggestedQuestions: [
      'An bhféadfá eolas a thabhairt dom faoi Buiséad 2026?',
      'Céard iad mo chuid cearta teanga mar shaoránach in Éirinn?',
      'An bhfuil clár aclaíochta ar fáil do dhaoine atá ag dul in aois?'
    ],
    // Optional disclaimer text shown below the input field
    disclaimerText: undefined,
    disclaimerLinkText: undefined,
    disclaimerLinkUrl: undefined,
    disclaimerAfterLinkText: undefined,
    disclaimerPhoneNumber: undefined
  },
  
  // Chat input
  chatInput: {
    placeholder: 'Cuir ceist orm anseo...',
    ariaLabel: 'Clóscríobh do theachtaireacht chuig HubChat',
    clearButton: 'Glan',
    sendButton: 'Seol',
    voiceInput: {
      preparingPlaceholder: 'Ag ullmhú an mhicreafóin...',
      listeningPlaceholder: 'Ag éisteacht... Labhair anois.',
      processingPlaceholder: 'Ag próiseáil ionchur gutha...',
      preparingStatus: 'Ag ullmhú...',
      listeningStatus: 'Labhair anois',
      connectingAriaLabel: 'Ionchur gutha. Tosaíonn nó stopann Alt agus M an taifeadadh; cuireann Escape ar ceal é.',
      stopRecordingAriaLabel: 'Stop an taifeadadh',
      processingAriaLabel: 'Ag próiseáil ionchur gutha',
      startRecordingAriaLabel: 'Ionchur gutha. Tosaíonn nó stopann Alt agus M an taifeadadh; cuireann Escape ar ceal é.',
      cancelRecordingAriaLabel: 'Cealaigh taifeadadh',
      cancelRecordingTitle: 'Cealaigh taifeadadh',
      clearInputAriaLabel: 'Glan téacs ionchuir',
      clearInputTitle: 'Glan téacs',
      unsupportedBrowserMessage: 'Níl ionchur gutha ar fáil sa bhrabhsálaí seo. Is féidir leat do cheist a chlóscríobh fós.',
      recordingAnnouncement: 'Taifeadadh',
      recordingCancelledAnnouncement: 'Cuireadh an taifeadadh ar ceal',
      silenceDetectedAnnouncement: 'Braitheadh ciúnas. Stopadh an taifeadadh.',
      processingVoiceInputLabel: 'Ag próiseáil ionchur gutha',
      recordingTooShortMessage: 'Tá an taifeadadh ró-ghearr. Bain triail eile as.',
      recordingTooShortAnnouncement: 'Bhí an taifeadadh ró-ghearr',
      transcriptionResultPrefix: 'Seo a chualamar:',
      noSpeechDetectedMessage: 'Níor braitheadh aon chaint. Bain triail eile as.',
      noSpeechDetectedAnnouncement: 'Níor braitheadh aon chaint',
      transcriptionFailedMessage: 'Theip ar an tras-scríobh',
      errorPrefix: 'Earráid:',
      recordingFailedMessage: 'Theip ar an taifeadadh. Bain triail eile as.',
      microphoneAccessDeniedMessage: 'Diúltaíodh rochtain ar an micreafón. Ceadaigh é i socruithe an bhrabhsálaí.',
      noMicrophoneDetectedMessage: 'Níor aimsíodh micreafón.',
      microphoneAccessFailedMessage: 'Níorbh fhéidir rochtain a fháil ar an micreafón.'
    },
    clearConfirmDialog: {
      title: 'Glan an comhrá?',
      description: 'Scriosfaidh sé seo do stair comhrá ar fad. Ní féidir é seo a chealú.',
      cancelButton: 'Cealaigh',
      confirmButton: 'Glan'
    }
  },
  
  // Answer actions
  actions: {
    buttons: {
      like: 'Is maith liom an freagra seo',
      dislike: 'Ní maith liom an freagra seo',
      copy: 'Cóipeáil freagra',
      viewThoughtProcess: 'Féach ar an bpróiseas smaointe',
      playAudio: 'Seinn fuaime',
      stopAudio: 'Stop fuaime',
      thisAnswer: 'an freagra seo',
      moreActions: 'Tuilleadh gníomhartha',
      closeDialog: 'Dún'
    },
    menu: {
      exportMarkdown: 'Easpórtáil - Markdown',
      exportText: 'Easpórtáil - Téacs',
      report: 'Tuairiscigh'
    },
    reportDialog: {
      title: 'Tuairiscigh Teachtaireacht',
      description: 'Úsáid an fhoirm seo chun aiseolas a thabhairt nó fadhb leis an bhfreagra seo a thuairisciú.',
      reasonLabel: 'Cúis',
      detailsLabel: 'Sonraí (Roghnach)',
      cancelButton: 'Cealaigh',
      submitButton: 'Cuir isteach'
    },
    toasts: {
      feedbackSubmittedLike: 'Aiseolas curtha isteach (is maith liom).',
      feedbackSubmittedDislike: 'Aiseolas curtha isteach (ní maith liom).',
      errorSendingLike: 'Earráid ag cur aiseolais is maith liom: ',
      errorSendingDislike: 'Earráid ag cur aiseolais ní maith liom: ',
      answerCopied: 'Freagra cóipeáilte go ghearrthaisce!',
      answerCopiedMarkdown: 'Freagra cóipeáilte mar Markdown go ghearrthaisce!',
      answerExportedText: 'Freagra easpórtáilte mar théacs!',
      reportSubmitted: 'Tuairisc curtha isteach go rathúil.',
      errorSubmittingReport: 'Earráid ag cur tuairisce isteach: ',
      playingAudio: 'Ag seinm fuaime...',
      failedToSynthesize: 'Teip ag sintéisiú cainte: '
    },
    labels: {
      eventHistory: 'Stair Imeachtaí:'
    }
  },
  
  // Messages
  messages: {
    processing: 'Ag próiseáil:',
    initializing: 'Ag cuardach...',
    userMessageLabel: 'Do cheist',
    botResponseLabel: 'Freagra ó HubChat'
  },
  
  // Events
  events: {
    buttonTitle: 'Imeachtaí',
    historyLabel: 'Stair Imeachtaí:'
  },
  
  // Follow-up
  followUp: {
    ariaLabelPrefix: 'Fiafraigh: '
  },
  
  // Citations
  citations: {
    viewSourcePrefix: 'Féach ar fhoinse: ',
    // UTM tracking for analytics - appended to all citation URLs
    citationUtmParams: 'utm_source=hubchat-app&utm_medium=chatbot&utm_campaign=citation'
  },
  
  // Accessibility settings
  accessibility: {
    skipLinkText: 'Scipeáil go dtí an príomhábhar',
    homeLabel: 'Baile',
    opensInNewTab: 'osclaítear i gcluaisín nua',
    versionLabel: 'Leagan',
  },
  
  // Screen reader announcements (WCAG 2.1 Level AA) - Irish localization
  screenReader: {
    userAsks: 'Fiafraíonn an t-úsáideoir:',
    processing: 'Tá HubChat ag cuardach freagra...',
    streaming: 'Tá HubChat ag clóscríobh freagra...',
    responseSays: 'Deir HubChat:',
    withSources: 'le {count} tagairt foinse',
    withFollowUps: '{count} ceisteanna leantacha ar fáil',
    responseComplete: 'Freagra críochnaithe.',
  },
  
  // Rate limiting (WCAG 2.2 AAA compliant) - Irish localization
  rateLimiting: {
    friendly: {
      title: 'Moilligh, le do thoil',
      message: 'Tá tú ag cur ceisteanna róthapaidh. Fan tamall beag sular triail tú arís. Cuidíonn sé seo linn freagraí ardcháilíochta a sholáthar do chách.',
      countdown: 'Is féidir leat teachtaireacht eile a sheoladh i gceann {seconds} soicind',
      screenReaderAnnouncement: 'Teorainn ráta bainte amach. Fan {seconds} soicind sula seolann tú teachtaireacht eile.',
      extensionButton: 'Iarr am breise'
    },
    warning: {
      message: 'Tá tú ag seoladh ceisteanna an-tapa. Moilligh chun teorannú sealadach a sheachaint.',
      threshold: 0.8  // Warn at 80% of limit
    },
    accessibility: {
      errorRole: 'alert' as const,
      livePriority: 'assertive' as const,
      countdownRole: 'timer' as const
    }
  },
  
  // Government-specific settings
  government: {
    complianceMode: true,
    auditRequired: true,
    dataRetention: '7years',
    accessibilityLevel: 'WCAG-AA',
  },

  locales: {
    en: {
      appName: 'HubChat',
      appTitle: 'HubChat',
      appDescription: 'A trusted AI information source for public services, regulations, benefits, and rights.',
      header: {
        voiceSettings: {
          buttonTitle: 'Voice Settings',
          voiceSelectionLabel: 'Voice Selection',
          briefLabel: 'Brief Response',
          prefetchLabel: 'Prefetch speech audio',
          speedLabel: 'Playback speed',
          speedOptions: {
            normal: 'Normal',
            faster: 'Faster'
          },
          selectedPrefix: 'Selected: ',
          defaultVoice: 'Default Voice'
        },
        languageSwitcher: {
          groupLabel: 'Language switcher',
          englishLabel: 'English',
          irishLabel: 'Irish',
          englishAriaLabel: 'Switch language to English',
          irishAriaLabel: 'Switch language to Irish'
        }
      },
      footer: {
        leftText: 'Privacy Statement',
        centerText: 'Udaras na Gaeltachta',
        rightText: 'About: HubChat - '
      },
      hero: {
        title: 'Welcome to HubChat',
        subtitle: 'A trusted AI information source for public services, regulations, benefits, and rights.',
        tryAskingLabel: 'Not sure where to start? Try asking:',
        suggestedQuestions: [
          'Can you explain Budget 2026?',
          'What are my Irish language rights as a citizen in Ireland?',
          'Are there any fitness programmes for older adults?'
        ]
      },
      chatInput: {
        placeholder: 'Type your question here...',
        ariaLabel: 'Type your message to HubChat chatbot',
        clearButton: 'Clear',
        sendButton: 'Send',
        voiceInput: {
          preparingPlaceholder: 'Preparing microphone...',
          listeningPlaceholder: 'Listening... Speak now.',
          processingPlaceholder: 'Processing voice input...',
          preparingStatus: 'Preparing...',
          listeningStatus: 'Speak now',
          connectingAriaLabel: 'Voice input. Alt plus M starts or stops recording; Escape cancels.',
          stopRecordingAriaLabel: 'Stop recording',
          processingAriaLabel: 'Processing voice input',
          startRecordingAriaLabel: 'Voice input. Alt plus M starts or stops recording; Escape cancels.',
          cancelRecordingAriaLabel: 'Cancel recording',
          cancelRecordingTitle: 'Cancel recording',
          clearInputAriaLabel: 'Clear input text',
          clearInputTitle: 'Clear text',
          unsupportedBrowserMessage: 'Voice input is not available in this browser. You can still type your question.',
          recordingAnnouncement: 'Recording',
          recordingCancelledAnnouncement: 'Recording cancelled',
          silenceDetectedAnnouncement: 'Silence detected. Recording stopped.',
          processingVoiceInputLabel: 'Processing voice input',
          recordingTooShortMessage: 'Recording too short. Please try again.',
          recordingTooShortAnnouncement: 'Recording was too short',
          transcriptionResultPrefix: 'This is what we heard:',
          noSpeechDetectedMessage: 'No speech detected. Please try again.',
          noSpeechDetectedAnnouncement: 'No speech detected',
          transcriptionFailedMessage: 'Transcription failed',
          errorPrefix: 'Error:',
          recordingFailedMessage: 'Recording failed. Please try again.',
          microphoneAccessDeniedMessage: 'Microphone access denied. Please allow it in browser settings.',
          noMicrophoneDetectedMessage: 'No microphone detected.',
          microphoneAccessFailedMessage: 'Could not access microphone.'
        },
        clearConfirmDialog: {
          title: 'Clear conversation?',
          description: 'This will delete your full conversation history. This cannot be undone.',
          cancelButton: 'Cancel',
          confirmButton: 'Clear'
        }
      },
      actions: {
        buttons: {
          like: 'Like this answer',
          dislike: 'Dislike this answer',
          copy: 'Copy answer',
          viewThoughtProcess: 'View thought process',
          playAudio: 'Play audio',
          stopAudio: 'Stop audio',
          thisAnswer: 'this answer',
          moreActions: 'More actions',
          closeDialog: 'Close'
        },
        menu: {
          exportMarkdown: 'Export - Markdown',
          exportText: 'Export - Text',
          report: 'Report'
        },
        reportDialog: {
          title: 'Report Message',
          description: 'Use this form to provide feedback or report an issue with this answer.',
          reasonLabel: 'Reason',
          detailsLabel: 'Details (Optional)',
          cancelButton: 'Cancel',
          submitButton: 'Submit'
        },
        toasts: {
          feedbackSubmittedLike: 'Feedback submitted (like).',
          feedbackSubmittedDislike: 'Feedback submitted (dislike).',
          errorSendingLike: 'Error sending like feedback: ',
          errorSendingDislike: 'Error sending dislike feedback: ',
          answerCopied: 'Answer copied to clipboard!',
          answerCopiedMarkdown: 'Answer copied as Markdown to clipboard!',
          answerExportedText: 'Answer exported as text file!',
          reportSubmitted: 'Report submitted successfully.',
          errorSubmittingReport: 'Error submitting report: ',
          playingAudio: 'Playing audio...',
          failedToSynthesize: 'Failed to synthesize speech: '
        },
        labels: {
          eventHistory: 'Event History:'
        }
      },
      messages: {
        processing: 'Processing:',
        initializing: 'Searching...',
        userMessageLabel: 'Your question',
        botResponseLabel: 'Response from HubChat'
      },
      events: {
        buttonTitle: 'Events',
        historyLabel: 'Event History:'
      },
      followUp: {
        ariaLabelPrefix: 'Ask: '
      },
      citations: {
        viewSourcePrefix: 'View source: '
      },
      accessibility: {
        skipLinkText: 'Skip to main content',
        homeLabel: 'Home',
        opensInNewTab: 'opens in new tab',
        versionLabel: 'Version'
      },
      screenReader: {
        userAsks: 'User asks:',
        processing: 'HubChat is searching for an answer...',
        streaming: 'HubChat is typing a response...',
        responseSays: 'HubChat says:',
        withSources: 'with {count} source references',
        withFollowUps: '{count} follow-up questions available',
        responseComplete: 'Response complete.'
      },
      rateLimiting: {
        friendly: {
          title: 'Please slow down',
          message: 'You are sending questions too quickly. Please wait a moment before trying again.',
          countdown: 'You can send another message in {seconds} seconds',
          screenReaderAnnouncement: 'Rate limit reached. Please wait {seconds} seconds before sending another message.',
          extensionButton: 'Request additional time'
        },
        warning: {
          message: 'You are sending questions very quickly. Please slow down to avoid temporary limits.'
        }
      }
    }
  }
} as const;

export type UdarasBrandConfig = typeof udarasBrandConfig;
